<template>
  <div class="flex flex-col gap-4 md:gap-8">
    <slot name="before-sidebar"></slot>
    <!-- Secondary Blocks from Contentful in correct order -->
    <template v-if="secondaryBlocks?.length">
      <div v-for="(block, index) in secondaryBlocks" :key="`secondary-${index}`" class="">
        <!-- CTA Block -->
        <cta-banner
          v-if="(block as any).sys?.contentType?.sys?.id === 'cta'"
          :cta-data="block as any"
          theme="default"
          variant="sidebar"
          :mascot-position="(block as any).fields?.maskotPlacement?.[0] || 'right'"
        />

        <!-- Text Block -->
        <sidebar-text-block
          v-else-if="(block as any).sys?.contentType?.sys?.id === 'textBlock'"
          :text-block-data="block"
        />
      </div>
    </template>

    <!-- Quiz for djurfakta articles -->
    <quiz v-if="isDjurfaktaArticle" variant="sidebar" />

    <!-- Related Articles Slider -->
    <article-slider
      v-if="shouldShowSlider"
      :category-id="categoryId"
      :portal-id="portalId"
      :exclude-slug="currentSlug"
      :slider-type="sliderType"
    />

    <slot name="after-sidebar"></slot>
  </div>
</template>

<script setup lang="ts">
import type { Article } from '@/types/contentful'

interface ArticleSidebarProps {
  // Article data
  article?: Article
  // Slider configuration
  portalPath?: string
  portalTitle?: string
  categoryId?: string
  portalId?: string
  // Slider visibility control
  showSlider?: boolean
  sliderType?: 'djurfakta' | 'category' | 'recipe' | 'default'
}

const props = withDefaults(defineProps<ArticleSidebarProps>(), {
  article: undefined,
  portalPath: undefined,
  portalTitle: undefined,
  categoryId: undefined,
  portalId: undefined,
  showSlider: true,
  sliderType: 'default',
})

// Get current slug from route to exclude it from the slider
const route = useRoute()
const currentSlug = computed(() => {
  return route.params.slug as string
})

// Determine if slider should be shown based on article type and route
const shouldShowSlider = computed(() => {
  if (!props.showSlider) return false

  const routePath = route.path

  // Show slider for djurfakta articles
  if (routePath.startsWith('/djurfakta/')) {
    return true
  }

  // Show slider for articles with categories (hjalp-djuren, vuxen)
  if (routePath.includes('/hjalp-djuren/') || routePath.includes('/vuxen/')) {
    return true
  }

  // Show slider for recipe articles under /hjalp-djuren/laga-vego
  if (routePath.includes('/hjalp-djuren/laga-vego/')) {
    return true
  }

  // Show slider for om-oss articles
  if (routePath.startsWith('/om-oss/')) {
    return true
  }

  return false
})

// Determine if this is a djurfakta article to show the quiz
const isDjurfaktaArticle = computed(() => {
  const routePath = route.path
  return routePath.startsWith('/djurfakta/')
})

// Extract secondary blocks in correct order (CTA and text blocks)
const secondaryBlocks = computed(() => {
  if (!props.article?.fields?.secondary) return []

  return props.article.fields.secondary.filter(
    (entry: any) =>
      entry.sys?.contentType?.sys?.id === 'cta' || entry.sys?.contentType?.sys?.id === 'textBlock',
  )
})
</script>
